import { LootEntry } from '../data/monsters.config';
import { RolledDrop, WeightedEntry } from '../types/loot.types';

/**
 * System 1 — independent. Each entry rolls its own chance; on success a
 * random quantity in [quantityMin, quantityMax] is produced. `multiplier`
 * scales both chance and quantity. `maxCount` caps the number of entries
 * that drop (location chests/urns). When `firstKill` is set, entries flagged
 * `guaranteedOnFirstKill` drop unconditionally (boss reward grimoires).
 */
export function rollIndependentPool(
  entries: LootEntry[],
  opts: { multiplier?: number; maxCount?: number; firstKill?: boolean } = {},
): RolledDrop[] {
  const multiplier = opts.multiplier ?? 1;
  const drops: RolledDrop[] = [];
  for (const entry of entries) {
    if (opts.maxCount !== undefined && drops.length >= opts.maxCount) {
      break;
    }
    const guaranteed =
      entry.guaranteedOnFirstKill === true && opts.firstKill === true;
    if (!guaranteed && Math.random() >= entry.chance * multiplier) {
      continue;
    }
    const qty =
      entry.quantityMin +
      Math.floor(Math.random() * (entry.quantityMax - entry.quantityMin + 1));
    drops.push({
      itemId: entry.itemId,
      quantity: Math.max(1, Math.round(qty * multiplier)),
    });
  }
  return drops;
}

/**
 * System 2 — weighted, guaranteed. Performs `round(count * multiplier)`
 * draws; each draw weighted-picks among entries still under their `max`,
 * adding one unit (max re-checked each draw). Stops early if the pool is
 * exhausted. Result is aggregated per item.
 */
export function rollWeightedPool(
  entries: WeightedEntry[],
  count: number,
  opts: { multiplier?: number } = {},
): RolledDrop[] {
  const draws = Math.max(0, Math.round(count * (opts.multiplier ?? 1)));
  const counts = new Map<string, number>();
  for (let i = 0; i < draws; i++) {
    const eligible = entries.filter((e) => (counts.get(e.itemId) ?? 0) < e.max);
    if (eligible.length === 0) {
      break;
    }
    const totalWeight = eligible.reduce((s, e) => s + e.weight, 0);
    let roll = Math.random() * totalWeight;
    let chosen = eligible[eligible.length - 1];
    for (const e of eligible) {
      roll -= e.weight;
      if (roll <= 0) {
        chosen = e;
        break;
      }
    }
    counts.set(chosen.itemId, (counts.get(chosen.itemId) ?? 0) + 1);
  }
  return Array.from(counts.entries()).map(([itemId, quantity]) => ({
    itemId,
    quantity,
  }));
}
